import type { MaterialSrRef } from './materials'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

export type CategoryAnalysisRawStatus =
  | 'failure'
  | 'in_process'
  | 'pending'
  | 'success'

export interface CategoryAnalysisTaskListParams {
  endDate?: string
  firstCategory?: string
  name?: string
  orderByUpdatedTime?: 'asc' | 'desc'
  pageIndex?: number
  pageSize?: number
  startDate?: string
  statuses?: CategoryAnalysisRawStatus[]
}

export interface CategoryAnalysisUpsertPayload {
  firstCategory: string
  id?: number
  materials?: MaterialSrRef[]
  name: string
}

export interface CategoryAnalysisTaskKeywordRawItem {
  ctr?: number | string
  cvr?: number | string
  lctr?: null | number | string
  lcvr?: null | number | string
  materials?: MaterialSrRef[]
  materialIds?: number[]
  name?: string
  roas?: number | string
  spend?: number | string
  videoCount?: number
}

export interface CategoryAnalysisTaskListItemRaw {
  firstCategory?: string
  errorInfo?: string
  id?: number
  lctr?: null | number | string
  lcvr?: null | number | string
  materials?: Array<MaterialSrRef & { status?: CategoryAnalysisRawStatus }>
  materialIds?: number[]
  name?: string
  status?: CategoryAnalysisRawStatus
  storageUrl?: string
  updatedTime?: string
}

export interface CategoryAnalysisTaskDetailRaw {
  conversionValue?: number | string
  copyHook?: CategoryAnalysisTaskKeywordRawItem[]
  copyHooks?: CategoryAnalysisTaskKeywordRawItem[]
  firstCategory?: string
  errorInfo?: string
  id?: number
  materials?: Array<MaterialSrRef & { status?: CategoryAnalysisRawStatus }>
  materialIds?: number[]
  name?: string
  painPoints?: CategoryAnalysisTaskKeywordRawItem[]
  roas?: number | string
  spend?: number | string
  status?: CategoryAnalysisRawStatus
  storageUrl?: string
  updatedTime?: string
  usps?: CategoryAnalysisTaskKeywordRawItem[]
  videoCount?: number
  visualHook?: CategoryAnalysisTaskKeywordRawItem[]
  visualHooks?: CategoryAnalysisTaskKeywordRawItem[]
}

export interface CategoryAnalysisTaskListResponseRaw {
  data?: {
    list?: CategoryAnalysisTaskListItemRaw[]
    pageIndex?: number
    pageSize?: number
    total?: number
  }
  list?: CategoryAnalysisTaskListItemRaw[]
  pageIndex?: number
  pageSize?: number
  total?: number
}

export interface CategoryAnalysisTaskPollItemRaw {
  id?: number
  status?: CategoryAnalysisRawStatus
}

export interface CategoryAnalysisTaskPollResponseRaw {
  data?: CategoryAnalysisTaskPollItemRaw[]
  failureIds?: number[]
  successIds?: number[]
}

export interface CategoryAnalysisTaskUpsertResponseRaw {
  data?: {
    id?: number
  }
  id?: number
}

type CategoryAnalysisTaskListResult = ReturnType<
  typeof normalizeCategoryAnalysisTaskListResponse
>
type CategoryAnalysisTaskDetailResult = ReturnType<
  typeof normalizeCategoryAnalysisTaskDetailResponse
>
type CategoryAnalysisTaskPollResult = ReturnType<
  typeof normalizeCategoryAnalysisTaskPollResponse
>

const toNumberId = (value: unknown) => {
  const next = Number(value)
  return Number.isInteger(next) && next > 0 ? next : 0
}

const toMetricNumber = (value: unknown) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : 0
}

const normalizeMaterialRefs = (value: unknown): MaterialSrRef[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      const payload =
        item && typeof item === 'object' ? (item as Partial<MaterialSrRef>) : {}
      return {
        materialId: String(payload.materialId || '').trim(),
        mediaPlatform: String(payload.mediaPlatform || '').trim(),
      }
    })
    .filter((item) => item.materialId && item.mediaPlatform)
}

const materialRefsToIds = (materials: MaterialSrRef[]) => {
  return materials
    .map((item) => Number(item.materialId))
    .filter((materialId) => Number.isInteger(materialId) && materialId > 0)
}

const normalizeMaterialIds = (
  materialIds: unknown,
  materials: MaterialSrRef[],
) => {
  if (materials.length > 0) return materialRefsToIds(materials)
  if (!Array.isArray(materialIds)) return []
  return materialIds
    .map(Number)
    .filter((materialId) => Number.isInteger(materialId) && materialId > 0)
}

const normalizeKeywordItems = (
  value: CategoryAnalysisTaskKeywordRawItem[] | undefined,
) => {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => {
      const materials = normalizeMaterialRefs(item.materials)
      return {
        materials,
        materialIds: normalizeMaterialIds(item.materialIds, materials),
        name: String(item.name || '').trim(),
        ctr: toMetricNumber(item.lctr),
        cvr: toMetricNumber(item.lcvr),
        roas: toMetricNumber(item.roas),
        spend: toMetricNumber(item.spend),
        videoCount: Number(item.videoCount || 0),
      }
    })
    .filter((item) => item.name)
}

export const normalizeCategoryAnalysisTaskListResponse = (
  response:
    | CategoryAnalysisTaskListItemRaw[]
    | CategoryAnalysisTaskListResponseRaw,
  params: CategoryAnalysisTaskListParams,
) => {
  if (Array.isArray(response)) {
    return {
      items: response,
      pageIndex: Number(params.pageIndex || 1),
      pageSize: Number(params.pageSize || 20),
      total: response.length,
    }
  }

  const list =
    response?.list ||
    response?.data?.list ||
    ([] as CategoryAnalysisTaskListItemRaw[])

  return {
    items: list,
    pageIndex: Number(
      response?.pageIndex || response?.data?.pageIndex || params.pageIndex || 1,
    ),
    pageSize: Number(
      response?.pageSize || response?.data?.pageSize || params.pageSize || 20,
    ),
    total: Number(
      response?.total || response?.data?.total || Math.max(list.length, 0),
    ),
  }
}

export const normalizeCategoryAnalysisTaskDetailResponse = (
  response:
    | CategoryAnalysisTaskDetailRaw
    | { data?: CategoryAnalysisTaskDetailRaw },
) => {
  const data =
    response &&
    typeof response === 'object' &&
    'data' in response &&
    response.data
      ? response.data
      : response
  const payload = (data || {}) as CategoryAnalysisTaskDetailRaw
  const materials = normalizeMaterialRefs(payload.materials)

  return {
    conversionValue: payload.conversionValue ?? '0',
    copyHooks: normalizeKeywordItems(payload.copyHooks || payload.copyHook),
    firstCategory: String(payload.firstCategory || '').trim(),
    id: toNumberId(payload.id),
    errorInfo: String(payload.errorInfo || '').trim(),
    materials,
    materialIds: normalizeMaterialIds(payload.materialIds, materials),
    name: String(payload.name || '').trim(),
    painPoints: normalizeKeywordItems(payload.painPoints),
    roas: payload.roas ?? '0',
    spend: payload.spend ?? '0',
    status: (payload.status || 'pending') as CategoryAnalysisRawStatus,
    storageUrl: String(payload.storageUrl || '').trim(),
    updatedTime: String(payload.updatedTime || '').trim(),
    usps: normalizeKeywordItems(payload.usps),
    videoCount: Number(payload.videoCount || 0),
    visualHooks: normalizeKeywordItems(
      payload.visualHooks || payload.visualHook,
    ),
  }
}

export const normalizeCategoryAnalysisTaskPollResponse = (
  response:
    | CategoryAnalysisTaskPollItemRaw[]
    | CategoryAnalysisTaskPollResponseRaw
    | undefined,
) => {
  let data: CategoryAnalysisTaskPollItemRaw[] = []

  if (Array.isArray(response)) {
    data = response
  } else if (Array.isArray(response?.data)) {
    data = response.data
  } else if (response && typeof response === 'object') {
    // SR 轮询只返回终态 id 列表，这里转成页面原本消费的 id/status 结构，
    // 让组件无需关心后端在任务轮询接口上的返回形态差异。
    const successItems = Array.isArray(response.successIds)
      ? response.successIds.map((id) => ({
          id,
          status: 'success' as CategoryAnalysisRawStatus,
        }))
      : []
    const failureItems = Array.isArray(response.failureIds)
      ? response.failureIds.map((id) => ({
          id,
          status: 'failure' as CategoryAnalysisRawStatus,
        }))
      : []
    data = [...successItems, ...failureItems]
  }

  return data
    .map((item) => ({
      id: toNumberId(item?.id),
      status: (item?.status || 'pending') as CategoryAnalysisRawStatus,
    }))
    .filter((item) => item.id > 0)
}

export const normalizeCategoryAnalysisTaskUpsertResponse = (
  response: CategoryAnalysisTaskUpsertResponseRaw | undefined,
) => {
  return toNumberId(response?.data?.id || response?.id)
}

export const materialAnalysisFirstCategoryAggrTasksApi = defineApi(
  'operation',
  ({ prefix }) => {
    const requestTaskList = (params: CategoryAnalysisTaskListParams) => {
      // Mock fallback note:
      // during earlier local development this route was proxied in vite to the
      // backend-mock service; if backend becomes unavailable again, restore the
      // commented proxy block in apps/web-antd/vite.config.mts.
      return requestClient
        .get<
          | CategoryAnalysisTaskListItemRaw[]
          | CategoryAnalysisTaskListResponseRaw
        >(prefix('materials/first-category-aggr-sr-tasks'), {
          params,
        })
        .then((res) => normalizeCategoryAnalysisTaskListResponse(res, params))
    }

    const requestTaskDetail = (
      id: number | string,
      params: {
        endDate?: string
        startDate?: string
      },
    ) => {
      return requestClient
        .get<
          | CategoryAnalysisTaskDetailRaw
          | { data?: CategoryAnalysisTaskDetailRaw }
        >(prefix(`materials/first-category-aggr-sr-tasks/${id}`), {
          params,
        })
        .then((res) => normalizeCategoryAnalysisTaskDetailResponse(res))
    }

    const requestTaskStatus = (taskIds: number[]) => {
      return requestClient
        .post<
          | CategoryAnalysisTaskPollItemRaw[]
          | CategoryAnalysisTaskPollResponseRaw
        >(prefix('materials/first-category-aggr-sr-tasks/poll-status'), {
          taskIds,
        })
        .then((res) => normalizeCategoryAnalysisTaskPollResponse(res))
    }

    const requestUpsertTask = (payload: CategoryAnalysisUpsertPayload) => {
      return requestClient
        .put<CategoryAnalysisTaskUpsertResponseRaw>(
          prefix('materials/first-category-aggr-sr-tasks'),
          payload,
        )
        .then((res) => normalizeCategoryAnalysisTaskUpsertResponse(res))
    }

    const requestDeleteTask = (id: number) => {
      return requestClient.delete(
        prefix(`materials/first-category-aggr-sr-tasks/${id}`),
      )
    }

    const requestSubmitTask = (id: number) => {
      return requestClient.post(
        prefix('materials/first-category-aggr-sr-tasks/submit'),
        {
          taskId: id,
        },
      )
    }

    function getTaskList(
      params: CategoryAnalysisTaskListParams,
    ): Promise<CategoryAnalysisTaskListResult> {
      return requestTaskList(params)
    }

    function getTaskDetail(
      id: number | string,
      params: {
        endDate?: string
        startDate?: string
      },
    ): Promise<CategoryAnalysisTaskDetailResult> {
      return requestTaskDetail(id, params)
    }

    function pollTaskStatus(
      taskIds: number[],
    ): Promise<CategoryAnalysisTaskPollResult> {
      return requestTaskStatus(taskIds)
    }

    function upsertTask(
      payload: CategoryAnalysisUpsertPayload,
    ): Promise<number> {
      return requestUpsertTask(payload)
    }

    function deleteTask(id: number) {
      return requestDeleteTask(id)
    }

    function submitTask(id: number) {
      return requestSubmitTask(id)
    }

    return {
      deleteTask,
      getTaskDetail,
      getTaskList,
      pollTaskStatus,
      redoTask: submitTask,
      submitTask,
      upsertTask,
    }
  },
)
